import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import {
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  CheckCircle as CheckCircleIcon,
  CloudDownload as CloudDownloadIcon,
  Description as DescriptionIcon,
  EditNote as EditNoteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Gavel as GavelIcon,
  History as HistoryIcon,
  Logout as LogoutIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { AuthContext } from "../../../contexts/AuthContext";
import { useErrorDialog } from "../../../contexts/ErrorDialogContext";
import FormCSSubmissionDialog from "./CsForm/FormCSSubmissionDialog";
import FormCSDeclarationSection, {
  FORM_CS_DECLARATION_ITEMS,
  FORM_CS_FINAL_CONFIRMATION_LABEL,
} from "./CsForm/FormCSDeclarationSection";
import { openFormCSPdfInNewTab } from "./CsForm/FormCSDraftReviewPdf";
import { generateFormCSDraftReviewPdf } from "./CsForm/Pdf/generator";
import {
  downloadFormCSSubmittedPdf,
  getSubmissionRecordId,
} from "./CsForm/Pdf/formCSSubmissionAuditPdf";
import { setSubscriptionStatus } from "../../../utils/pdf/subscriptionStatus";
import ComponentLoading from "../../App/ComponentLoading";
import ConfirmationDialog from "../../Console-Panel/RecycleBin/ConfirmationDialog";
import BaseDialog from "../../utils/dialog/BaseDialog";
import CorpPassAuthCard from "./CorpPassAuthCard";
import TaxAgentAuthCard from "./TaxAgentAuthCard";
import {
  getCompanySubmissionHistory,
  getSubmissionHistoryById,
  getTaxDraft,
  getTaxPrefill,
  saveTaxDraft,
  submitFormCS,
} from "../../../contexts/services/iras/irasApiServices";
import { disconnectIras } from "../../../contexts/services/iras/irasAuthServices";
import {
  applyPrefillPayload,
  buildFormCSPayload,
  buildFormCSPrefillPayload,
  createInitialDataFormCS,
  createInitialDeclaration,
  createInitialDeclarationAck,
  createInitialFilingInfo,
  formatIrasApiError,
  formatIrasSubmissionError,
  isDeclarationStepValid,
} from "./CsForm/formCSDefaults";
import {
  formatFormCSValidationErrors,
  validateFormCSDataForm,
} from "./CsForm/formCSValidation";
import { getDataFormCSFieldMeta } from "./CsForm/formCSDataFormFields";
import FormCSDevPdfExportPanel from "./CsForm/FormCSDevPdfExportPanel";
import { IRAS_CONFIG } from "../../../config/irasConfig";

const ENABLE_PRE_SUBMIT_VALIDATION = Boolean(
  IRAS_CONFIG?.FEATURE_FLAGS?.ENABLE_FORM_CS_PRE_SUBMIT_VALIDATION
);
const AUTO_PREFILL_SUPPRESS_STORAGE_PREFIX =
  "iras_formcs_auto_prefill_suppress";
const IRAS_INVITATION_SCREEN_ENABLED = ["1", "true", "yes", "on"].includes(
  `${process.env.REACT_APP_IRAS_INVITATION_SCREEN_ENABLED || ""}`
    .trim()
    .toLowerCase()
);
const IRAS_INVITATION_KEY = `${
  process.env.REACT_APP_IRAS_INVITATION_KEY || ""
}`.trim();
const IRAS_INVITATION_STORAGE_KEY = "iras_formcs_invitation_key";

const FILING_INFO_LABELS = {
  uen: "UEN",
  uenType: "UEN type",
  ya: "Year of assessment",
  designationOfFiler: "Designation of filer",
  nameOfContactPerson: "Contact person",
  contactNumber: "Contact number",
  designationOfContactPerson: "Contact person designation",
  declarantRefNo: "Declarant reference no.",
};

const DECLARATION_LABELS = {
  isRevBelow5M: "Revenue is within Form C-S threshold",
  isOnlyIncTxbl17: "Income taxable at 17%",
  isNotClaimingSpecificItems: "Not claiming specified items",
  isFullAndTrueAccOfCompInc: "Full and true account declared",
  isQualifiedToUseConvFormCS: "Qualified to use conventional Form C-S",
};

const normalizeComparableValue = (value) => {
  if (value == null) return "";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return `${value}`;
  return `${value}`.trim();
};

const formatRetrievedValue = (value) => {
  if (value == null || value === "") return "Blank";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return `${value}`;
};

const getRetrievedSectionSource = (payload, section) => {
  if (!payload || typeof payload !== "object") return null;
  if (section === "dataFormCS") {
    return payload.dataFormCS && typeof payload.dataFormCS === "object"
      ? payload.dataFormCS
      : payload.data && typeof payload.data === "object"
        ? payload.data
        : null;
  }
  return payload[section] && typeof payload[section] === "object"
    ? payload[section]
    : null;
};

const buildPrefillResultItems = (retrievedPayload, before, after) => {
  const sectionConfig = [
    {
      key: "filingInfo",
      title: "Filing information",
      getLabel: (field) => FILING_INFO_LABELS[field] || field,
    },
    {
      key: "dataFormCS",
      title: "Form C-S data",
      getLabel: (field) => getDataFormCSFieldMeta(field).label,
    },
    {
      key: "declaration",
      title: "Declarations",
      getLabel: (field) => DECLARATION_LABELS[field] || field,
    },
  ];

  return sectionConfig.flatMap(({ key, title, getLabel }) => {
    const source = getRetrievedSectionSource(retrievedPayload, key);
    if (!source) return [];

    return Object.keys(source)
      .filter((field) =>
        Object.prototype.hasOwnProperty.call(after?.[key] || {}, field)
      )
      .map((field) => ({
        id: `${key}.${field}`,
        section: title,
        label: getLabel(field),
        value: formatRetrievedValue(after[key][field]),
        changed:
          normalizeComparableValue(before?.[key]?.[field]) !==
          normalizeComparableValue(after?.[key]?.[field]),
      }));
  });
};

const formatDraftDate = (value) => {
  if (!value) return "No saved draft";
  return new Intl.DateTimeFormat("en-SG", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
};

const formatSubmissionDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-SG", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatDeclarationDate = (value) => {
  if (!value) return "-";
  const dateOnlyMatch = `${value}`.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const date = dateOnlyMatch
    ? new Date(
        Number(dateOnlyMatch[1]),
        Number(dateOnlyMatch[2]) - 1,
        Number(dateOnlyMatch[3])
      )
    : new Date(value);
  if (Number.isNaN(date.getTime())) return `${value}`;
  return new Intl.DateTimeFormat("en-SG", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const resolveCompanyId = (company) =>
  company?.c_id || company?.company_id || company?.companyId || company?.id;

const extractSubmissionHistory = (response) => {
  const root = response?.data ?? response;
  const candidates = [
    root,
    root?.data,
    root?.history,
    root?.records,
    root?.submissions,
    root?.submissionHistory,
    root?.data?.history,
    root?.data?.records,
    root?.data?.submissions,
    root?.data?.submissionHistory,
  ];
  return candidates.find(Array.isArray) || [];
};

const getSubmissionDateValue = (record = {}) =>
  record.created_at ||
  record.createdAt ||
  record.submitted_at ||
  record.submittedAt ||
  record.updated_at ||
  record.updatedAt;

const getSubmissionStatusValue = (record = {}) => {
  if (record.is_success === true || record.success === true) return "success";
  if (record.is_success === false || record.success === false) return "failed";
  return (
    record.status ||
    record.submission_status ||
    record.submissionStatus ||
    record.result ||
    record.iras_status ||
    record.irasStatus ||
    "recorded"
  );
};

const formatSubmissionStatus = (record) => {
  const status = `${getSubmissionStatusValue(record)}`.trim();
  if (!status) return "Recorded";
  return status
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getSubmissionStatusColor = (record) => {
  const status = `${getSubmissionStatusValue(record)}`.toLowerCase();
  if (
    status.includes("success") ||
    status.includes("submitted") ||
    status.includes("accepted")
  )
    return "success";
  if (
    status.includes("fail") ||
    status.includes("error") ||
    status.includes("reject")
  )
    return "error";
  if (status.includes("pending") || status.includes("processing"))
    return "warning";
  return "default";
};

const getSubmissionYa = (record = {}) =>
  record.ya ||
  record.yearOfAssessment ||
  record.filingInfo?.ya ||
  record.payload?.filingInfo?.ya ||
  record.request_payload?.filingInfo?.ya;

const getSubmissionAcknowledgementNo = (record = {}) => {
  const response = record.response || record.iras_response || record.irasResponse;
  const responseData =
    response?.data && typeof response.data === "object"
      ? response.data
      : response;
  return (
    record.acknowledgementNo ||
    record.acknowledgement_no ||
    record.acknowledgement_number ||
    record.ackNo ||
    record.ack_no ||
    record.iras_acknowledgement_no ||
    responseData?.acknowledgementNo ||
    responseData?.ackNo ||
    responseData?.acknowledgement_number ||
    response?.acknowledgementNo ||
    response?.ackNo ||
    ""
  );
};

const formatFormCSSuccessMessage = (data = {}) => {
  const nested = data?.iras_response?.data || {};
  const acknowledgementNo =
    data.acknowledgementNo ||
    data.ackNo ||
    data.acknowledgement_number ||
    nested.acknowledgementNo;
  const estTaxPayableRaw =
    data.estTaxPayable ??
    data.estimatedTaxPayable ??
    data.est_tax_payable ??
    data.finalEstimatedTaxPayable ??
    nested.estTaxPayable;
  const estTaxPayable =
    estTaxPayableRaw == null || estTaxPayableRaw === ""
      ? ""
      : String(estTaxPayableRaw);
  const submissionDate =
    data.submissionDate ||
    data.submittedAt ||
    data.timestamp ||
    nested.timestamp;

  const details = [
    acknowledgementNo ? `Acknowledgement ${acknowledgementNo}` : null,
    estTaxPayable ? `Estimated Tax Payable ${estTaxPayable}` : null,
    submissionDate ? `Submission Date ${submissionDate}` : null,
  ].filter(Boolean);

  return `Form C-S submitted successfully${details.length ? ` - ${details.join(" | ")}` : ""}.`;
};

const formatSubmissionDateTime = (value) => {
  if (!value) return "";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleString();
};

const hasRequiredFilingInfo = (filingInfo = {}) =>
  Boolean(
    filingInfo.uen &&
    filingInfo.ya !== "" &&
    filingInfo.ya != null &&
    !Number.isNaN(Number(filingInfo.ya)) &&
    filingInfo.designationOfFiler &&
    filingInfo.nameOfContactPerson &&
    filingInfo.contactNumber &&
    filingInfo.designationOfContactPerson
  );

const getSubmissionMessage = (record = {}) =>
  record.message ||
  record.error_message ||
  record.errorMessage ||
  record.response_message ||
  record.responseMessage ||
  record.iras_message ||
  record.irasMessage ||
  "";

const getSubmissionCreator = (record = {}) =>
  record.created_by_user || record.createdByUser || record.creator || null;

const getSubmissionDeclarationAck = (record = {}) =>
  record.internal_declaration_ack || record.internalDeclarationAck || {};

const SubmissionRecord = ({
  record,
  compact = false,
  onDownloadPdf,
  isDownloading = false,
  downloadDisabled = false,
}) => {
  const ya = getSubmissionYa(record);
  const acknowledgementNo = getSubmissionAcknowledgementNo(record);
  const message = getSubmissionMessage(record);
  const creator = getSubmissionCreator(record);
  const declarationAck = getSubmissionDeclarationAck(record);
  const submissionId = getSubmissionRecordId(record);
  const canDownload = Boolean(onDownloadPdf && submissionId);
  const creatorName = creator?.name || creator?.email || record.created_by;
  const creatorEmail =
    creator?.name && creator?.email && creator.name !== creator.email
      ? creator.email
      : "";
  const declarantName =
    declarationAck.declarantName || declarationAck.declarant_name || "";
  const declarationDate =
    declarationAck.declarationDate || declarationAck.declaration_date || "";
  const submitterCapacity =
    declarationAck.submitterCapacity === "TAX_AGENT"
      ? "Authorised tax agent"
      : declarationAck.submitterCapacity === "TAXPAYER"
        ? "Company representative"
        : "Not recorded";

  return (
    <Box
      sx={(theme) => ({
        p: compact ? 0 : 2.25,
        borderRadius: 1.5,
        border: compact
          ? "none"
          : `1px solid ${alpha(theme.palette.divider, 0.16)}`,
        bgcolor: compact ? "transparent" : "background.paper",
      })}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent='space-between'
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant='body2' sx={{ fontWeight: 700 }}>
            {ya ? `Form C-S YA ${ya}` : "Form C-S"}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {formatSubmissionDate(getSubmissionDateValue(record))}
          </Typography>
          {compact && (
            <Typography
              variant='caption'
              color={acknowledgementNo ? "text.primary" : "text.secondary"}
              sx={{
                display: "block",
                fontWeight: acknowledgementNo ? 600 : 400,
              }}
            >
              {acknowledgementNo
                ? `Acknowledgement No. ${acknowledgementNo}`
                : "Acknowledgement No. —"}
            </Typography>
          )}
        </Box>
        <Stack
          direction='row'
          spacing={0.75}
          alignItems='center'
          sx={{ flexShrink: 0 }}
        >
          <Chip
            size='small'
            color={getSubmissionStatusColor(record)}
            label={formatSubmissionStatus(record)}
            sx={{ fontWeight: 600 }}
          />
          {onDownloadPdf && (
            <Tooltip
              title={
                canDownload
                  ? "Download submitted Form C-S"
                  : "This record cannot be downloaded."
              }
            >
              <span>
                <IconButton
                  size='small'
                  onClick={() => onDownloadPdf(record)}
                  disabled={!canDownload || downloadDisabled}
                  aria-label='Download submitted Form C-S'
                >
                  {isDownloading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <PictureAsPdfIcon fontSize='small' />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {!compact && (
        <Stack
          spacing={1.5}
          sx={(theme) => ({
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          })}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
              },
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: "block", fontWeight: 700 }}
              >
                Acknowledgement No.
              </Typography>
              <Typography
                variant='body2'
                sx={{ fontWeight: 600 }}
                color={acknowledgementNo ? "text.primary" : "text.secondary"}
              >
                {acknowledgementNo || "—"}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: "block", fontWeight: 700 }}
              >
                Submitted by
              </Typography>
              <Typography variant='body2' sx={{ fontWeight: 600 }}>
                {creatorName || "Unknown user"}
              </Typography>
              {creatorEmail && (
                <Typography variant='caption' color='text.secondary'>
                  {creatorEmail}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: "block", fontWeight: 700 }}
              >
                Internal declarant
              </Typography>
              <Typography variant='body2' sx={{ fontWeight: 600 }}>
                {declarantName || "Not recorded"}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Capacity: {submitterCapacity}
              </Typography>
              {declarationDate && (
                <Typography variant='caption' color='text.secondary'>
                  Declaration date: {formatDeclarationDate(declarationDate)}
                </Typography>
              )}
            </Box>
          </Box>

          <Box>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: "block", fontWeight: 700, mb: 0.25 }}
            >
              Submission remarks
            </Typography>
            <Typography
              variant='body2'
              color={message ? "text.primary" : "text.secondary"}
              sx={{ lineHeight: 1.5 }}
            >
              {message || "Submission attempt recorded."}
            </Typography>
          </Box>
        </Stack>
      )}
    </Box>
  );
};

const todayDateString = () => {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

const IRASDashboard = ({
  company = {},
  isAuthenticated = false,
  isAuthChecking = false,
  authError = "",
  onDisconnect,
  onBackToTaxComputation,
  getTaxComputationList,
}) => {
  const { clearIrasCredentials, global_authState } = useContext(AuthContext);
  const handleError = useErrorDialog();
  const companyId = resolveCompanyId(company);
  const autoPrefillAttemptedRef = useRef(false);
  const suppressAutoPrefillRef = useRef(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isPrefilling, setIsPrefilling] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpeningForm, setIsOpeningForm] = useState(false);
  const [formCSDialogOpen, setFormCSDialogOpen] = useState(false);
  const [taxDraft, setTaxDraft] = useState(null);
  const [draftQuery, setDraftQuery] = useState(null);
  const [submissionHistory, setSubmissionHistory] = useState([]);
  const [submissionHistoryError, setSubmissionHistoryError] = useState("");
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [downloadingSubmissionId, setDownloadingSubmissionId] = useState("");
  const [prefillConfirmOpen, setPrefillConfirmOpen] = useState(false);
  const [prefillResultOpen, setPrefillResultOpen] = useState(false);
  const [prefillResultItems, setPrefillResultItems] = useState([]);
  const [prefillResultSource, setPrefillResultSource] = useState(null);
  const [invitationKeyInput, setInvitationKeyInput] = useState("");
  const [invitationError, setInvitationError] = useState("");
  const [invitationAccepted, setInvitationAccepted] = useState(() => {
    if (!IRAS_INVITATION_SCREEN_ENABLED) return true;
    if (!IRAS_INVITATION_KEY) return false;
    try {
      return (
        localStorage.getItem(IRAS_INVITATION_STORAGE_KEY) ===
        IRAS_INVITATION_KEY
      );
    } catch {
      return false;
    }
  });
  const [submitDeclarationOpen, setSubmitDeclarationOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [submitSuccessDialogOpen, setSubmitSuccessDialogOpen] = useState(false);
  const [showSubmitDeclarationDetails, setShowSubmitDeclarationDetails] =
    useState(false);
  const [submitSuccessMessage, setSubmitSuccessMessage] = useState("");
  const [submitSuccessDetails, setSubmitSuccessDetails] = useState({
    acknowledgementNo: "",
    estimatedTaxPayable: "",
    submissionDate: "",
    filingInfo: {},
    dataFormCS: {},
    declaration: {},
    declarationAck: {},
  });
  const [submitFormState, setSubmitFormState] = useState(null);
  const [submitDeclaration, setSubmitDeclaration] = useState(() =>
    createInitialDeclaration()
  );
  const [submitDeclarationAck, setSubmitDeclarationAck] = useState(() =>
    createInitialDeclarationAck()
  );

  const getAutoPrefillSuppressStorageKey = useCallback(() => {
    const suffix = companyId || company?.uen || "unknown";
    return `${AUTO_PREFILL_SUPPRESS_STORAGE_PREFIX}:${suffix}`;
  }, [company?.uen, companyId]);

  const isAutoPrefillSuppressed = useCallback(() => {
    if (suppressAutoPrefillRef.current) return true;
    try {
      return sessionStorage.getItem(getAutoPrefillSuppressStorageKey()) === "1";
    } catch {
      return false;
    }
  }, [getAutoPrefillSuppressStorageKey]);

  const markAutoPrefillSuppressed = useCallback(() => {
    suppressAutoPrefillRef.current = true;
    try {
      sessionStorage.setItem(getAutoPrefillSuppressStorageKey(), "1");
    } catch {
      /* ignore */
    }
  }, [getAutoPrefillSuppressStorageKey]);

  const resolveDraftQuery = useCallback(async () => {
    const uen = company?.uen ? `${company.uen}`.trim() : "";
    if (!uen) return null;
    return { uen };
  }, [company?.uen]);

  const loadDraft = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (silent) setIsRefreshing(true);
        setSubmissionHistoryError("");

        const query = await resolveDraftQuery();
        setDraftQuery(query);

        const [draftResult, historyResult] = await Promise.allSettled([
          query ? getTaxDraft(query) : Promise.resolve(null),
          companyId
            ? getCompanySubmissionHistory(companyId)
            : Promise.resolve(null),
        ]);

        let loadedDraft = null;
        if (!query) {
          setTaxDraft(null);
        } else if (draftResult.status === "fulfilled") {
          loadedDraft = draftResult.value?.data || null;
          setTaxDraft(loadedDraft);
        } else {
          console.error("Failed to load Form C-S draft:", draftResult.reason);
          handleError("Failed to load Form C-S draft");
        }

        if (!companyId) {
          setSubmissionHistory([]);
        } else if (historyResult.status === "fulfilled") {
          setSubmissionHistory(extractSubmissionHistory(historyResult.value));
        } else {
          console.error(
            "Failed to load Form C-S submission history:",
            historyResult.reason
          );
          setSubmissionHistory([]);
          setSubmissionHistoryError("Failed to load submission history.");
        }

        return loadedDraft;
      } catch (error) {
        console.error("Failed to load Form C-S dashboard:", error);
        handleError("Failed to load Form C-S dashboard");
        return null;
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [companyId, handleError, resolveDraftQuery]
  );

  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  useEffect(() => {
    autoPrefillAttemptedRef.current = false;
    suppressAutoPrefillRef.current = isAutoPrefillSuppressed();
    setPrefillResultOpen(false);
    setPrefillResultItems([]);
    setPrefillResultSource(null);
  }, [companyId, isAuthenticated, isAutoPrefillSuppressed]);

  const draftPayload = taxDraft?.payload || null;
  const draftFilingInfo = draftPayload?.filingInfo || {};
  const draftYa = taxDraft?.ya || draftFilingInfo?.ya || draftQuery?.ya;
  const draftUen = taxDraft?.uen || draftFilingInfo?.uen || draftQuery?.uen;

  const buildBaseFormState = useCallback(
    () => ({
      filingInfo: createInitialFilingInfo(company, global_authState),
      declaration: createInitialDeclaration(),
      declarationAck: createInitialDeclarationAck(),
      dataFormCS: createInitialDataFormCS(),
    }),
    [company, global_authState]
  );

  const resolveDraftFormState = useCallback(
    (payload = draftPayload) =>
      payload
        ? applyPrefillPayload(payload, buildBaseFormState())
        : buildBaseFormState(),
    [buildBaseFormState, draftPayload]
  );

  const sortedSubmissionHistory = useMemo(
    () =>
      [...submissionHistory].sort((left, right) => {
        const leftTime = new Date(getSubmissionDateValue(left)).getTime();
        const rightTime = new Date(getSubmissionDateValue(right)).getTime();
        return (
          (Number.isNaN(rightTime) ? 0 : rightTime) -
          (Number.isNaN(leftTime) ? 0 : leftTime)
        );
      }),
    [submissionHistory]
  );
  const latestSubmission = sortedSubmissionHistory[0] || null;
  const updatedPrefillFieldCount = prefillResultItems.filter(
    (item) => item.changed
  ).length;
  const matchingPrefillFieldCount =
    prefillResultItems.length - updatedPrefillFieldCount;
  const invitationGateLocked =
    IRAS_INVITATION_SCREEN_ENABLED && !invitationAccepted;

  const handleInvitationSubmit = () => {
    if (!IRAS_INVITATION_KEY) {
      setInvitationError(
        "Invitation key is not configured. Please contact the administrator."
      );
      return;
    }

    if (invitationKeyInput.trim() !== IRAS_INVITATION_KEY) {
      setInvitationError("Invitation key is invalid.");
      return;
    }

    try {
      localStorage.setItem(IRAS_INVITATION_STORAGE_KEY, IRAS_INVITATION_KEY);
    } catch {
      /* ignore */
    }
    setInvitationAccepted(true);
    setInvitationError("");
    setInvitationKeyInput("");
    setToastMessage("Invitation accepted. IRAS live testing is available.");
  };

  const handleSubmissionSuccess = (
    submissionData,
    formState,
    declaration,
    declarationAck
  ) => {
    const nested = submissionData?.iras_response?.data || {};
    const acknowledgementNo =
      submissionData?.acknowledgementNo ||
      submissionData?.ackNo ||
      submissionData?.acknowledgement_number ||
      nested?.acknowledgementNo ||
      "";
    const estimatedTaxPayable =
      submissionData?.estTaxPayable ??
      submissionData?.estimatedTaxPayable ??
      submissionData?.est_tax_payable ??
      submissionData?.finalEstimatedTaxPayable ??
      nested?.estTaxPayable ??
      "";
    const submissionDate =
      submissionData?.submissionDate ||
      submissionData?.submittedAt ||
      submissionData?.timestamp ||
      nested?.timestamp ||
      "";

    setSubmitSuccessDetails({
      acknowledgementNo: acknowledgementNo ? String(acknowledgementNo) : "",
      estimatedTaxPayable:
        estimatedTaxPayable === "" || estimatedTaxPayable == null
          ? ""
          : String(estimatedTaxPayable),
      submissionDate: submissionDate ? String(submissionDate) : "",
      filingInfo: { ...(formState?.filingInfo || {}) },
      dataFormCS: { ...(formState?.dataFormCS || {}) },
      declaration: { ...(declaration || {}) },
      declarationAck: { ...(declarationAck || {}) },
    });
    setShowSubmitDeclarationDetails(false);
    setSubmitSuccessMessage(formatFormCSSuccessMessage(submissionData));
    setSubmitSuccessDialogOpen(true);
  };

  const closeSubmitSuccessDialog = () => {
    setSubmitSuccessDialogOpen(false);
    setShowSubmitDeclarationDetails(false);
    setSubmitSuccessDetails({
      acknowledgementNo: "",
      estimatedTaxPayable: "",
      submissionDate: "",
      filingInfo: {},
      dataFormCS: {},
      declaration: {},
      declarationAck: {},
    });
  };

  const handleViewSubmittedFormCS = () => {
    try {
      const doc = generateFormCSDraftReviewPdf(
        true,
        company,
        {
          filingInfo: submitSuccessDetails.filingInfo,
          dataFormCS: submitSuccessDetails.dataFormCS,
          declaration: submitSuccessDetails.declaration,
          declarationAck: submitSuccessDetails.declarationAck,
        },
        {
          documentStatus: "submitted",
          acknowledgementNo: submitSuccessDetails.acknowledgementNo,
          estTaxPayable: submitSuccessDetails.estimatedTaxPayable,
        }
      );
      openFormCSPdfInNewTab(doc, "Form C-S Submitted");
    } catch (error) {
      console.error("Submitted Form C-S PDF failed:", error);
      handleError("Unable to generate the submitted Form C-S PDF.");
    }
  };

  const handleDownloadSubmittedFormCS = useCallback(
    async (record) => {
      const submissionId = getSubmissionRecordId(record);
      if (!submissionId || downloadingSubmissionId) return;

      try {
        setDownloadingSubmissionId(String(submissionId));
        setSubscriptionStatus(global_authState?.subscription?.status || null);
        const response = await getSubmissionHistoryById(submissionId);
        downloadFormCSSubmittedPdf({
          apiResponse: response,
          fallbackRecord: record,
          company,
        });
        setToastMessage("Submitted Form C-S downloaded.");
      } catch (error) {
        console.error("Failed to download submitted Form C-S:", error);
        handleError(
          formatIrasApiError(
            error,
            "Unable to download the submitted Form C-S. Please try again."
          )
        );
      } finally {
        setDownloadingSubmissionId("");
      }
    },
    [
      company,
      downloadingSubmissionId,
      global_authState?.subscription?.status,
      handleError,
    ]
  );

  const handleDisconnect = async () => {
    if (isDisconnecting) return;
    try {
      setIsDisconnecting(true);
      const response = await disconnectIras();
      if (response?.status !== "success") {
        throw new Error(response?.message || "Failed to disconnect from IRAS");
      }
      clearIrasCredentials();
      setPrefillResultOpen(false);
      setPrefillResultItems([]);
      setPrefillResultSource(null);
      await loadDraft({ silent: true });
      setToastMessage("Disconnected from IRAS successfully.");
      onDisconnect?.();
    } catch (error) {
      console.error("Failed to disconnect from IRAS:", error);
      handleError(error?.message || "Failed to disconnect from IRAS");
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleOpenForm = async () => {
    if (isOpeningForm) return;
    if (invitationGateLocked) {
      handleError(
        "Please enter a valid invitation key to access IRAS live testing."
      );
      return;
    }
    try {
      setIsOpeningForm(true);
      await loadDraft({ silent: true });
      setFormCSDialogOpen(true);
    } finally {
      setIsOpeningForm(false);
    }
  };

  const handleCloseForm = () => {
    setFormCSDialogOpen(false);
  };

  const handleConfirmPrefill = useCallback(
    async ({ automatic = false } = {}) => {
      if (isPrefilling) return;
      if (invitationGateLocked) return;
      if (automatic && isAutoPrefillSuppressed()) return;
      if (!isAuthenticated) {
        handleError(
          "Please connect CorpPass before retrieving data from IRAS."
        );
        setPrefillConfirmOpen(false);
        return;
      }
      try {
        setIsPrefilling(true);
        const currentState = resolveDraftFormState();
        const payload = buildFormCSPrefillPayload(currentState);
        const response = await getTaxPrefill(payload);
        if (response?.status !== "success") {
          throw response || new Error("Could not retrieve data from IRAS.");
        }

        const merged =
          response.data?.merged_payload ??
          response.data?.iras_prefill ??
          response.data;
        const applied = applyPrefillPayload(merged, currentState);
        const retrievedItems = buildPrefillResultItems(
          merged,
          currentState,
          applied
        );
        await saveTaxDraft(
          buildFormCSPayload({
            filingInfo: applied.filingInfo,
            declaration: applied.declaration,
            dataFormCS: applied.dataFormCS,
            declarationAck: applied.declarationAck,
          })
        );
        if (automatic && isAutoPrefillSuppressed()) {
          return;
        }
        setPrefillConfirmOpen(false);
        setPrefillResultItems(retrievedItems);
        setPrefillResultSource({
          uen: payload.filingInfo?.uen || "",
          basisPeriodFrom: payload.data?.basisPeriodFrom || "",
          basisPeriodTo: payload.data?.basisPeriodTo || "",
        });
        setPrefillResultOpen(retrievedItems.length > 0);
        setToastMessage(
          retrievedItems.length > 0
            ? `${retrievedItems.length} IRAS field${retrievedItems.length === 1 ? "" : "s"} retrieved.`
            : automatic
              ? "IRAS data loaded into an editable Form C-S draft."
              : "Draft refreshed with the latest available IRAS data."
        );
        await loadDraft({ silent: true });
      } catch (error) {
        console.warn("IRAS data retrieval unavailable:", error);
        if (automatic) {
          setToastMessage(
            "IRAS data could not be loaded. You can retry or continue manually."
          );
        } else {
          handleError(
            formatIrasApiError(
              error,
              "IRAS data retrieval is unavailable. Please complete Form C-S manually."
            )
          );
        }
      } finally {
        setIsPrefilling(false);
      }
    },
    [
      handleError,
      isAuthenticated,
      isAutoPrefillSuppressed,
      isPrefilling,
      invitationGateLocked,
      loadDraft,
      resolveDraftFormState,
    ]
  );

  useEffect(() => {
    if (
      !isLoading &&
      isAuthenticated &&
      !invitationGateLocked &&
      !taxDraft &&
      !autoPrefillAttemptedRef.current &&
      !isAutoPrefillSuppressed()
    ) {
      autoPrefillAttemptedRef.current = true;
      handleConfirmPrefill({ automatic: true });
    }
  }, [
    handleConfirmPrefill,
    isAuthenticated,
    isAutoPrefillSuppressed,
    isLoading,
    invitationGateLocked,
    taxDraft,
  ]);

  const openSubmitDeclaration = () => {
    if (invitationGateLocked) {
      handleError(
        "Please enter a valid invitation key to access IRAS live testing."
      );
      return;
    }

    if (!isAuthenticated) {
      handleError(
        "Please connect CorpPass before submitting Form C-S to IRAS."
      );
      return;
    }

    if (!draftPayload) {
      handleError(
        "Please complete and save a Form C-S draft before submitting to IRAS."
      );
      return;
    }

    const formState = resolveDraftFormState();
    if (
      ENABLE_PRE_SUBMIT_VALIDATION &&
      !hasRequiredFilingInfo(formState.filingInfo)
    ) {
      handleError(
        "Please complete filing information and save the draft before submitting."
      );
      return;
    }

    if (ENABLE_PRE_SUBMIT_VALIDATION) {
      const dataValidation = validateFormCSDataForm(formState.dataFormCS);
      if (!dataValidation.isValid) {
        handleError(formatFormCSValidationErrors(dataValidation.errors));
        return;
      }
    }

    setSubmitFormState(formState);
    setSubmitDeclaration(formState.declaration || createInitialDeclaration());
    setSubmitDeclarationAck({
      ...createInitialDeclarationAck(),
      ...(formState.declarationAck || {}),
      declarationDate:
        formState.declarationAck?.declarationDate || todayDateString(),
    });
    setSubmitDeclarationOpen(true);
  };

  const handleConfirmSubmit = async () => {
    if (isSubmitting || !submitFormState) return;
    if (
      ENABLE_PRE_SUBMIT_VALIDATION &&
      !isDeclarationStepValid(submitDeclaration, submitDeclarationAck)
    ) {
      handleError(
        "Please complete all required declarations and signature fields before submitting."
      );
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = buildFormCSPayload({
        filingInfo: submitFormState.filingInfo,
        declaration: submitDeclaration,
        dataFormCS: submitFormState.dataFormCS,
        declarationAck: submitDeclarationAck,
      });
      await saveTaxDraft(payload);
      const response = await submitFormCS(payload, { skipAuthRefresh: true });
      if (response?.status !== "success") {
        throw response || new Error("Submission failed");
      }
      setSubmitDeclarationOpen(false);
      setPrefillResultOpen(false);
      setPrefillResultItems([]);
      setPrefillResultSource(null);
      // Prevent post-submit load/refresh from auto-triggering prefill
      // and overriding submission success feedback.
      markAutoPrefillSuppressed();
      handleSubmissionSuccess(
        response?.data,
        submitFormState,
        submitDeclaration,
        submitDeclarationAck
      );
      await loadDraft({ silent: true });
    } catch (error) {
      console.error("Form C-S submission failed:", error);
      handleError(formatIrasSubmissionError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <ComponentLoading message='Loading Form C-S...' />;
  }

  const actionDisabled =
    isAuthChecking ||
    invitationGateLocked ||
    !isAuthenticated ||
    isRefreshing ||
    isPrefilling ||
    isOpeningForm ||
    isSubmitting;
  const canSubmitDraft =
    !invitationGateLocked && isAuthenticated && Boolean(taxDraft);
  const workflowSteps = [
    {
      title: "Connect",
      description: isAuthenticated
        ? "CorpPass connected"
        : "Authorise IRAS access",
      complete: isAuthenticated,
      active: !isAuthenticated,
    },
    {
      title: "Prepare",
      description: taxDraft
        ? "Editable draft ready"
        : isAuthenticated
          ? "Retrieving IRAS data first"
          : "IRAS data retrieval starts after connection",
      complete: Boolean(taxDraft),
      active: isAuthenticated && !taxDraft,
    },
    {
      title: "Submit",
      description: canSubmitDraft
        ? "Review and declare"
        : "Available when draft is ready",
      complete: false,
      active: canSubmitDraft,
    },
  ];

  return (
    <Box
      sx={(theme) => ({
        p: { xs: 2, md: 3 },
        bgcolor: alpha(theme.palette.grey[100], 0.1),
        boxSizing: "border-box",
        height: { xs: "auto", xl: "calc(100vh - 180px)" },
        minHeight: { xs: "calc(100vh - 180px)", lg: 0 },
      })}
    >
      <Paper
        elevation={0}
        sx={(theme) => ({
          height: "100%",
          minHeight: 0,
          overflow: { xs: "visible", xl: "hidden" },
          borderRadius: 2.5,
          bgcolor: "background.paper",
          border: `1px solid ${alpha(theme.palette.divider, 0.16)}`,
          boxShadow: `0 18px 50px ${alpha(theme.palette.common.black, 0.045)}`,
        })}
      >
        <Stack sx={{ height: "100%", minHeight: 0 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent='space-between'
            sx={(theme) => ({
              px: { xs: 2.5, md: 3 },
              py: 2.25,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              flexShrink: 0,
            })}
          >
            <Stack direction='row' spacing={1.5} alignItems='center'>
              <Box
                sx={(theme) => ({
                  width: 44,
                  height: 44,
                  borderRadius: 1.25,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.09),
                })}
              >
                <EditNoteIcon />
              </Box>
              <Box>
                <Typography variant='h6' sx={{ fontWeight: 800 }}>
                  Form C-S Workspace
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {company?.name || "Selected company"}
                  {company?.uen ? ` - UEN ${company.uen}` : ""}
                </Typography>
              </Box>
            </Stack>
            <Stack direction='row' spacing={1} alignItems='center'>
              <Chip
                size='small'
                color={isAuthenticated ? "success" : "default"}
                variant={isAuthenticated ? "filled" : "outlined"}
                icon={isAuthenticated ? <CheckCircleIcon /> : undefined}
                label={
                  isAuthChecking
                    ? "Checking IRAS"
                    : isAuthenticated
                      ? "IRAS connected"
                      : "IRAS not connected"
                }
                sx={{ fontWeight: 700 }}
              />
              <Tooltip title='Refresh workspace'>
                <span>
                  <IconButton
                    size='small'
                    onClick={() => loadDraft({ silent: true })}
                    disabled={isRefreshing}
                  >
                    {isRefreshing ? (
                      <CircularProgress size={18} />
                    ) : (
                      <RefreshIcon fontSize='small' />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                lg: "minmax(0, 1fr)",
                xl: "minmax(0, 1fr) 300px",
              },
              flex: 1,
              minHeight: 0,
              overflow: { xs: "visible", xl: "hidden" },
            }}
          >
            {/* <Box
              sx={(theme) => ({
                p: 2.5,
                bgcolor: alpha(theme.palette.grey[100], 0.32),
                borderRight: {
                  lg: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                },
              })}
            >
              <Stack spacing={2.25} sx={{ position: { lg: "sticky" }, top: 0 }}>
                <Typography
                  variant='overline'
                  color='text.secondary'
                  sx={{ fontWeight: 800, letterSpacing: 0.8 }}
                >
                  Filing progress
                </Typography>
                {workflowSteps.map((step, index) => (
                  <Stack
                    key={step.title}
                    direction='row'
                    spacing={1.25}
                    alignItems='flex-start'
                  >
                    <Box
                      sx={(theme) => ({
                        width: 28,
                        height: 28,
                        flex: "0 0 28px",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: step.complete
                          ? theme.palette.success.main
                          : step.active
                            ? theme.palette.primary.main
                            : alpha(theme.palette.text.secondary, 0.08),
                        color:
                          step.complete || step.active
                            ? "#fff"
                            : theme.palette.text.secondary,
                        fontSize: 12,
                        fontWeight: 800,
                      })}
                    >
                      {step.complete ? (
                        <CheckCircleIcon sx={{ fontSize: 17 }} />
                      ) : (
                        index + 1
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant='body2' sx={{ fontWeight: 800 }}>
                        {step.title}
                      </Typography>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ display: "block", lineHeight: 1.4 }}
                      >
                        {step.description}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Box> */}

            <Box
              sx={{
                p: { xs: 2.5, md: 4 },
                minWidth: 0,
                overflowY: { xl: "auto" },
              }}
            >
              {!isAuthenticated ? (
                <Stack spacing={3}>
                  <Box>
                    <Typography
                      variant='h4'
                      sx={{ fontWeight: 800, letterSpacing: -0.5 }}
                    >
                      Start by connecting to IRAS{" "}
                      {IRAS_INVITATION_SCREEN_ENABLED && (
                        <Chip label='BETA' color='primary' />
                      )}
                    </Typography>
                    <Typography
                      variant='body1'
                      color='text.secondary'
                      sx={{ mt: 1, lineHeight: 1.7 }}
                    >
                      Once connected, we will first try to load available IRAS
                      data into an editable draft. Nothing is submitted until
                      you review and confirm it.
                    </Typography>
                  </Box>
                  {authError && (
                    <Typography variant='body2' color='error.main'>
                      {authError}
                    </Typography>
                  )}
                  <Stack
                    direction='row'
                    spacing={1.25}
                    alignItems='stretch'
                    sx={{
                      "& > *": {
                        flex: "1 1 0",
                        minWidth: 0,
                      },
                    }}
                  >
                    <CorpPassAuthCard company={company} compact />
                    <TaxAgentAuthCard company={company} compact />
                  </Stack>

                  <Box>
                    <Button
                      color='inherit'
                      onClick={handleOpenForm}
                      disabled={isOpeningForm || isRefreshing}
                      startIcon={
                        isOpeningForm ? (
                          <CircularProgress size={18} />
                        ) : (
                          <EditNoteIcon />
                        )
                      }
                      sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                      {isOpeningForm
                        ? "Loading draft..."
                        : "Continue with manual entry instead"}
                    </Button>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      Manual entry does not prevent you from connecting or
                      loading IRAS data later.
                    </Typography>
                  </Box>
                </Stack>
              ) : isPrefilling && !taxDraft ? (
                <Stack
                  spacing={2}
                  alignItems='center'
                  justifyContent='center'
                  sx={{ minHeight: 360, textAlign: "center" }}
                >
                  <CircularProgress color='success' />
                  <Box>
                    <Typography variant='h6' sx={{ fontWeight: 800 }}>
                      Loading your data from IRAS
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      We are creating an editable draft. You can review every
                      field before submission.
                    </Typography>
                  </Box>
                </Stack>
              ) : !taxDraft ? (
                <Stack spacing={3}>
                  <Box>
                    <Typography variant='h4' sx={{ fontWeight: 800 }}>
                      Complete your Form C-S draft
                    </Typography>
                    <Typography
                      variant='body1'
                      color='text.secondary'
                      sx={{ mt: 1, lineHeight: 1.7 }}
                    >
                      We could not create a draft automatically. Retrieve data
                      from IRAS again, or continue manually without losing the
                      option to retrieve the latest IRAS data later.
                    </Typography>
                  </Box>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <Button
                      variant='contained'
                      color='success'
                      size='large'
                      onClick={() => handleConfirmPrefill({ automatic: true })}
                      disabled={actionDisabled}
                      startIcon={<CloudDownloadIcon />}
                      sx={{ textTransform: "none", fontWeight: 800 }}
                    >
                      Retrieve Data from IRAS
                    </Button>
                    <Button
                      variant='outlined'
                      color='inherit'
                      size='large'
                      onClick={handleOpenForm}
                      disabled={isOpeningForm || isRefreshing}
                      startIcon={
                        isOpeningForm ? (
                          <CircularProgress size={18} />
                        ) : (
                          <EditNoteIcon />
                        )
                      }
                      sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                      {isOpeningForm ? "Loading draft..." : "Continue Manually"}
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Stack spacing={3}>
                  {/* Header */}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent='space-between'
                  >
                    <Box>
                      <Typography variant='h4' sx={{ fontWeight: 800 }}>
                        Review your tax return
                      </Typography>

                      <Typography
                        variant='body1'
                        color='text.secondary'
                        sx={{ mt: 0.75 }}
                      >
                        Your IRAS information has been retrieved. Review the
                        details, make any necessary changes, and complete your
                        declaration before submitting.
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Return summary */}
                  <Box
                    sx={(theme) => ({
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.035),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.13)}`,
                    })}
                  >
                    <Stack spacing={1}>
                      <Stack
                        direction='row'
                        alignItems='center'
                        justifyContent='space-between'
                      >
                        <Typography
                          variant='subtitle1'
                          sx={{ fontWeight: 800 }}
                        >
                          {draftYa ? `Form C-S · YA ${draftYa}` : "Form C-S"}
                        </Typography>

                        <Chip
                          size='small'
                          color='success'
                          label='IRAS data retrieved'
                          icon={<CheckCircleIcon />}
                        />
                      </Stack>

                      <Typography variant='body2' color='text.secondary'>
                        {draftUen ? `UEN ${draftUen} · ` : ""}
                        Last updated {formatDraftDate(taxDraft?.updated_at)}
                      </Typography>
                    </Stack>
                  </Box>

                  {/* Guided workflow */}
                  <Box>
                    <Typography variant='h6' sx={{ fontWeight: 800, mb: 2 }}>
                      Complete your tax return
                    </Typography>

                    <Stack spacing={2}>
                      {/* Step 1 */}
                      <Box
                        sx={(theme) => ({
                          p: 2.5,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                        })}
                      >
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                          justifyContent='space-between'
                          alignItems={{ xs: "flex-start", sm: "center" }}
                        >
                          <Stack
                            direction='row'
                            spacing={1.5}
                            alignItems='flex-start'
                          >
                            <CloudDownloadIcon color='primary' />

                            <Box>
                              <Typography sx={{ fontWeight: 800 }}>
                                Prefill Form C-S with IRAS data
                              </Typography>

                              <Typography
                                variant='body2'
                                color='text.secondary'
                                sx={{ mt: 0.5 }}
                              >
                                Import the available information from IRAS into
                                your Form C-S. You can review and edit the
                                imported information before submitting.
                              </Typography>
                            </Box>
                          </Stack>

                          <Button
                            variant='outlined'
                            size='small'
                            onClick={() => setPrefillConfirmOpen(true)}
                            disabled={actionDisabled}
                            startIcon={
                              isPrefilling ? (
                                <CircularProgress size={16} />
                              ) : (
                                <CloudDownloadIcon />
                              )
                            }
                            sx={{
                              textTransform: "none",
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {isPrefilling
                              ? "Prefilling..."
                              : "Prefill Form C-S"}
                          </Button>
                        </Stack>
                      </Box>

                      {/* Step 2 */}
                      <Box
                        sx={(theme) => ({
                          p: 2.5,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 2,
                        })}
                      >
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                          justifyContent='space-between'
                          alignItems={{ xs: "flex-start", sm: "center" }}
                        >
                          <Stack direction='row' spacing={1.5}>
                            <Box
                              sx={(theme) => ({
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main,
                                fontWeight: 800,
                                fontSize: 14,
                              })}
                            >
                              1
                            </Box>

                            <Box>
                              <Typography sx={{ fontWeight: 800 }}>
                                Review and edit your tax return
                              </Typography>

                              <Typography
                                variant='body2'
                                color='text.secondary'
                              >
                                Check the imported information and update any
                                fields that need correction.
                              </Typography>
                            </Box>
                          </Stack>

                          <Button
                            variant='contained'
                            size='large'
                            onClick={handleOpenForm}
                            disabled={isOpeningForm || isRefreshing}
                            startIcon={
                              isOpeningForm ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DescriptionIcon />
                              )
                            }
                            sx={{
                              textTransform: "none",
                              fontWeight: 800,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {isOpeningForm ? "Loading draft..." : "Review & Edit"}
                          </Button>
                        </Stack>
                      </Box>

                      {/* Step 3 */}
                      <Box
                        sx={{
                          p: 2.5,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                        }}
                      >
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                          justifyContent='space-between'
                          alignItems={{ xs: "flex-start", sm: "center" }}
                        >
                          <Stack direction='row' spacing={1.5}>
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "action.hover",
                                color: "text.secondary",
                                fontWeight: 800,
                                fontSize: 14,
                              }}
                            >
                              2
                            </Box>

                            <Box>
                              <Typography sx={{ fontWeight: 800 }}>
                                Review declaration and submit
                              </Typography>

                              <Typography
                                variant='body2'
                                color='text.secondary'
                              >
                                Confirm your declaration before sending the
                                completed return to IRAS.
                              </Typography>
                            </Box>
                          </Stack>

                          <Button
                            variant='contained'
                            color='success'
                            size='large'
                            onClick={openSubmitDeclaration}
                            disabled={actionDisabled}
                            startIcon={
                              isSubmitting ? (
                                <CircularProgress size={18} />
                              ) : (
                                <SendIcon />
                              )
                            }
                            sx={{
                              textTransform: "none",
                              fontWeight: 800,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Review & Submit
                          </Button>
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>

                  {/* Important information */}
                  <Box
                    sx={(theme) => ({
                      p: 2,
                      borderRadius: 2,
                      bgcolor: theme.palette.action.hover,
                    })}
                  >
                    <Typography variant='body2' color='text.secondary'>
                      <strong>Before submitting:</strong> Make sure all
                      information is accurate. Submission to IRAS will only take
                      place after you review and confirm the declaration.
                    </Typography>
                  </Box>
                </Stack>
              )}
            </Box>

            <Box
              sx={(theme) => ({
                p: 2.5,
                bgcolor: alpha(theme.palette.grey[100], 0.2),
                borderTop: {
                  xs: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                  xl: "none",
                },
                borderLeft: {
                  xl: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                },
                gridColumn: { lg: "1 / -1", xl: "auto" },
                overflowY: { xl: "auto" },
              })}
            >
              <Stack spacing={2.5}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                >
                  <Typography
                    variant='overline'
                    color='text.secondary'
                    sx={{ fontWeight: 800, letterSpacing: 0.8 }}
                  >
                    Workspace status
                  </Typography>
                  {isAuthenticated && (
                    <Button
                      size='small'
                      color='inherit'
                      startIcon={<LogoutIcon />}
                      onClick={handleDisconnect}
                      disabled={isDisconnecting}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      Disconnect
                    </Button>
                  )}
                </Stack>

                <Stack spacing={1}>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <SecurityIcon
                      color={isAuthenticated ? "success" : "disabled"}
                      fontSize='small'
                    />
                    <Typography variant='body2' sx={{ fontWeight: 800 }}>
                      {isAuthenticated
                        ? "IRAS connected"
                        : "IRAS not connected"}
                    </Typography>
                  </Stack>
                  <Typography variant='caption' color='text.secondary'>
                    {isAuthenticated
                      ? "IRAS data retrieval and submission are unlocked."
                      : "Manual entry is available. Connect to load IRAS data or submit."}
                  </Typography>
                </Stack>

                <Divider />

                <Stack spacing={1.25}>
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                  >
                    <Stack direction='row' spacing={0.75} alignItems='center'>
                      <HistoryIcon color='primary' fontSize='small' />
                      <Typography variant='subtitle2' sx={{ fontWeight: 800 }}>
                        Last submission
                      </Typography>
                    </Stack>
                    <Button
                      size='small'
                      onClick={() => setHistoryDialogOpen(true)}
                      disabled={!submissionHistory.length}
                      sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                      View all
                    </Button>
                  </Stack>
                  {submissionHistoryError ? (
                    <Typography variant='caption' color='warning.main'>
                      {submissionHistoryError}
                    </Typography>
                  ) : !latestSubmission ? (
                    <Typography variant='body2' color='text.secondary'>
                      No submissions yet.
                    </Typography>
                  ) : (
                    <SubmissionRecord
                      record={latestSubmission}
                      compact
                      onDownloadPdf={handleDownloadSubmittedFormCS}
                      isDownloading={
                        downloadingSubmissionId ===
                        String(getSubmissionRecordId(latestSubmission))
                      }
                      downloadDisabled={Boolean(downloadingSubmissionId)}
                    />
                  )}
                </Stack>

                {/* DEV-ONLY: remove FormCSDevPdfExportPanel import + block before production */}
                <FormCSDevPdfExportPanel
                  company={company}
                  onError={handleError}
                />
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Paper>

      <FormCSSubmissionDialog
        open={formCSDialogOpen}
        onClose={handleCloseForm}
        onDraftSaved={() => {
          setToastMessage("Draft saved successfully.");
          loadDraft({ silent: true });
        }}
        company={company}
        getTaxComputationList={getTaxComputationList}
        initialDraft={draftPayload}
        isIrasAuthenticated={isAuthenticated}
      />

      <ConfirmationDialog
        open={prefillConfirmOpen}
        onClose={() => {
          if (!isPrefilling) setPrefillConfirmOpen(false);
        }}
        onConfirm={handleConfirmPrefill}
        title='Retrieve latest data from IRAS?'
        message={`This retrieves available IRAS data for ${company?.uen || "this company"} and updates matching fields in the editable draft. You can review every change before submission.`}
        confirmText='Retrieve data'
        confirmColor='success'
        loading={isPrefilling}
      />

      <BaseDialog
        open={prefillResultOpen}
        onClose={() => setPrefillResultOpen(false)}
        maxWidth='md'
        title='Data Retrieved from IRAS'
        subtitle={`${updatedPrefillFieldCount} updated, ${matchingPrefillFieldCount} already matched your draft`}
        icon={<CloudDownloadIcon />}
        paperSx={{ overflow: "hidden" }}
        actions={
          <>
            <Button
              onClick={() => {
                setPrefillResultOpen(false);
                handleOpenForm();
              }}
              variant='contained'
              disabled={isOpeningForm || isRefreshing}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {isOpeningForm ? "Loading draft..." : "Review Form C-S"}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={() => setPrefillResultOpen(false)}>Close</Button>
          </>
        }
      >
        <Stack spacing={1.25} sx={{ maxHeight: "58vh", overflowY: "auto" }}>
          <Paper variant='outlined' sx={{ p: 1.5, borderRadius: 1.5 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1, sm: 3 }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant='caption' color='text.secondary'>
                  Retrieved for UEN
                </Typography>
                <Typography variant='body2' sx={{ fontWeight: 700 }}>
                  {prefillResultSource?.uen || "-"}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant='caption' color='text.secondary'>
                  Basis period from
                </Typography>
                <Typography variant='body2' sx={{ fontWeight: 700 }}>
                  {prefillResultSource?.basisPeriodFrom || "-"}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant='caption' color='text.secondary'>
                  Basis period to
                </Typography>
                <Typography variant='body2' sx={{ fontWeight: 700 }}>
                  {prefillResultSource?.basisPeriodTo || "-"}
                </Typography>
              </Box>
            </Stack>
          </Paper>
          {prefillResultItems.map((item) => (
            <Box
              key={item.id}
              sx={(theme) => ({
                p: 1.5,
                borderRadius: 1.5,
                border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
              })}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant='caption' color='text.secondary'>
                    {item.section}
                  </Typography>
                  <Typography variant='body2' sx={{ fontWeight: 700 }}>
                    {item.label}
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ overflowWrap: "anywhere" }}
                  >
                    {item.value}
                  </Typography>
                </Box>
                <Chip
                  size='small'
                  color={item.changed ? "success" : "default"}
                  variant={item.changed ? "filled" : "outlined"}
                  label={item.changed ? "Updated" : "Already matches"}
                  sx={{ flexShrink: 0, fontWeight: 600 }}
                />
              </Stack>
            </Box>
          ))}
        </Stack>
      </BaseDialog>

      <BaseDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth='md'
        title='Form C-S Submission History'
        subtitle='Submitted Form C-S records for this company'
        icon={<HistoryIcon />}
        paperSx={{ overflow: "hidden" }}
        actions={
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        }
      >
        <Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>
          <Stack spacing={1.5}>
            {sortedSubmissionHistory.map((record, index) => {
              const recordId = getSubmissionRecordId(record);
              return (
                <SubmissionRecord
                  key={
                    recordId ||
                    `${getSubmissionDateValue(record) || "record"}-${index}`
                  }
                  record={record}
                  onDownloadPdf={handleDownloadSubmittedFormCS}
                  isDownloading={
                    downloadingSubmissionId === String(recordId)
                  }
                  downloadDisabled={Boolean(downloadingSubmissionId)}
                />
              );
            })}
          </Stack>
        </Box>
      </BaseDialog>

      <BaseDialog
        open={submitDeclarationOpen}
        onClose={() => setSubmitDeclarationOpen(false)}
        closeDisabled={isSubmitting}
        maxWidth='lg'
        title='Declaration & Submission Confirmation'
        subtitle='Confirm authority and accuracy before sending Form C-S to IRAS'
        icon={<GavelIcon />}
        paperSx={{ overflow: "hidden" }}
        actions={
          <>
            <Button
              onClick={() => setSubmitDeclarationOpen(false)}
              disabled={isSubmitting}
              color='inherit'
            >
              Cancel
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant='contained'
              color='success'
              onClick={handleConfirmSubmit}
              disabled={
                isSubmitting ||
                (ENABLE_PRE_SUBMIT_VALIDATION &&
                  !isDeclarationStepValid(
                    submitDeclaration,
                    submitDeclarationAck
                  ))
              }
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={18} />
                ) : (
                  <AssignmentTurnedInIcon />
                )
              }
              sx={{ textTransform: "none", fontWeight: 700, minWidth: 180 }}
            >
              {isSubmitting ? "Submitting..." : "Confirm & Submit"}
            </Button>
          </>
        }
      >
        {submitFormState && (
          <FormCSDeclarationSection
            company={company}
            declaration={submitDeclaration}
            onChange={setSubmitDeclaration}
            declarationAck={submitDeclarationAck}
            onAckChange={setSubmitDeclarationAck}
            filingInfo={submitFormState.filingInfo}
            dataFormCS={submitFormState.dataFormCS}
            selectedTc={null}
            referenceSummary={null}
            disabled={isSubmitting}
          />
        )}
      </BaseDialog>

      <BaseDialog
        open={submitSuccessDialogOpen}
        onClose={closeSubmitSuccessDialog}
        maxWidth='md'
        title='Form C-S Submitted'
        subtitle='IRAS submission completed successfully.'
        actions={
          <>
            <Box sx={{ flex: 1 }} />
            <Button
              variant='contained'
              onClick={closeSubmitSuccessDialog}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              OK
            </Button>
          </>
        }
      >
        <Stack spacing={1.5}>
          <Typography
            variant='body1'
            color='text.primary'
            sx={{ fontWeight: 600 }}
          >
            Form C-S submitted successfully to IRAS.
          </Typography>
          <Box
            sx={(theme) => ({
              p: 1.5,
              borderRadius: 1.5,
              border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
              backgroundColor: alpha(theme.palette.success.main, 0.06),
            })}
          >
            <Stack spacing={0.75}>
              <Typography variant='body2' color='text.secondary'>
                Acknowledgement:{" "}
                <Box
                  component='span'
                  sx={{ color: "text.primary", fontWeight: 700 }}
                >
                  {submitSuccessDetails.acknowledgementNo || "-"}
                </Box>
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Estimated Tax Payable:{" "}
                <Box
                  component='span'
                  sx={{ color: "text.primary", fontWeight: 700 }}
                >
                  {submitSuccessDetails.estimatedTaxPayable || "-"}
                </Box>
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Submission Date:{" "}
                <Box
                  component='span'
                  sx={{ color: "text.primary", fontWeight: 700 }}
                >
                  {formatSubmissionDateTime(
                    submitSuccessDetails.submissionDate
                  ) || "-"}
                </Box>
              </Typography>
            </Stack>
          </Box>
          <Button
            variant='outlined'
            startIcon={<DescriptionIcon />}
            onClick={handleViewSubmittedFormCS}
            sx={{
              alignSelf: "flex-start",
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            View submitted Form C-S data PDF
          </Button>
          <Box>
            <Button
              color='inherit'
              size='small'
              onClick={() =>
                setShowSubmitDeclarationDetails((current) => !current)
              }
              endIcon={
                showSubmitDeclarationDetails ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )
              }
              aria-expanded={showSubmitDeclarationDetails}
              sx={{ px: 0, textTransform: "none", fontWeight: 700 }}
            >
              {showSubmitDeclarationDetails ? "Hide" : "Show"} declaration and
              acknowledgement details
            </Button>
            <Collapse in={showSubmitDeclarationDetails} unmountOnExit>
              <Stack spacing={2} sx={{ pt: 1.5 }}>
                <Box>
                  <Typography
                    variant='subtitle2'
                    sx={{ mb: 1, fontWeight: 700 }}
                  >
                    IRAS declarations
                  </Typography>
                  <Stack spacing={1}>
                    {FORM_CS_DECLARATION_ITEMS.map((item) => (
                      <Box
                        key={item.key}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <Chip
                          size='small'
                          color={
                            submitSuccessDetails.declaration[item.key]
                              ? "success"
                              : "default"
                          }
                          label={
                            submitSuccessDetails.declaration[item.key]
                              ? "Yes"
                              : "No"
                          }
                          sx={{ minWidth: 52, flexShrink: 0, fontWeight: 700 }}
                        />
                        <Typography variant='body2'>
                          {item.label}
                          {item.optional ? " (optional)" : ""}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>

                <Divider />

                <Box>
                  <Typography
                    variant='subtitle2'
                    sx={{ mb: 1, fontWeight: 700 }}
                  >
                    Acknowledgement details
                  </Typography>
                  <Stack spacing={1.25}>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <Chip
                        size='small'
                        color={
                          submitSuccessDetails.declarationAck
                            .isAuthorisedAccurate &&
                          submitSuccessDetails.declarationAck
                            .hasConfirmedAllDeclarations
                            ? "success"
                            : "default"
                        }
                        label={
                          submitSuccessDetails.declarationAck
                            .isAuthorisedAccurate &&
                          submitSuccessDetails.declarationAck
                            .hasConfirmedAllDeclarations
                            ? "Yes"
                            : "No"
                        }
                        sx={{ minWidth: 52, flexShrink: 0, fontWeight: 700 }}
                      />
                      <Typography variant='body2'>
                        {FORM_CS_FINAL_CONFIRMATION_LABEL}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant='caption' color='text.secondary'>
                        Declaration name
                      </Typography>
                      <Typography variant='body2' sx={{ fontWeight: 700 }}>
                        {submitSuccessDetails.declarationAck.declarantName ||
                          "-"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant='caption' color='text.secondary'>
                        Date of declaration
                      </Typography>
                      <Typography variant='body2' sx={{ fontWeight: 700 }}>
                        {formatDeclarationDate(
                          submitSuccessDetails.declarationAck.declarationDate
                        )}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </Collapse>
          </Box>
          <Typography variant='caption' color='text.secondary'>
            {submitSuccessMessage}
          </Typography>
        </Stack>
      </BaseDialog>

      <BaseDialog
        open={invitationGateLocked}
        closeDisabled
        maxWidth='sm'
        title='IRAS Live Testing Access'
        subtitle='Only invited users can access this feature before launch.'
        icon={<SecurityIcon />}
        actions={
          <>
            <Button
              onClick={onBackToTaxComputation}
              color='inherit'
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Back to Tax Computation
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant='contained'
              onClick={handleInvitationSubmit}
              disabled={!invitationKeyInput.trim() || !IRAS_INVITATION_KEY}
              sx={{ textTransform: "none", fontWeight: 700, minWidth: 150 }}
            >
              Continue
            </Button>
          </>
        }
      >
        <Stack spacing={2}>
          <Typography variant='body2' color='text.secondary'>
            We are preparing to launch IRAS Form C-S submission. This
            environment is currently live testing with IRAS and access is
            restricted to invited users only.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label='Invitation key'
            type='password'
            value={invitationKeyInput}
            onChange={(event) => {
              setInvitationKeyInput(event.target.value);
              if (invitationError) setInvitationError("");
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleInvitationSubmit();
              }
            }}
            error={Boolean(invitationError)}
            helperText={
              invitationError ||
              "Enter the key provided for the IRAS live testing period."
            }
          />
        </Stack>
      </BaseDialog>

      <Snackbar
        open={Boolean(toastMessage)}
        autoHideDuration={3500}
        onClose={() => setToastMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Paper
          elevation={8}
          sx={(theme) => ({
            px: 2,
            py: 1.5,
            maxWidth: 420,
            borderRadius: 1.5,
            color: theme.palette.common.white,
            bgcolor: theme.palette.grey[900],
          })}
        >
          <Typography variant='body2' sx={{ fontWeight: 600 }}>
            {toastMessage}
          </Typography>
        </Paper>
      </Snackbar>
    </Box>
  );
};

export default IRASDashboard;
